import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
    Button,
    Tooltip,
    useTheme,
    TextField,
    IconButton,
    Popover,
    Divider,
} from '@material-ui/core'
import { Add, Remove, Edit } from '@material-ui/icons'
import FlagIcon from '@material-ui/icons/Flag'

import { Container, SideDrawer } from '../../components'
import { CreateStatusPopover } from '../../components/CreateStatusPopover/CreateStatusPopover'
import { EditStatusPopover } from '../../components/EditStatusPopover/EditStatusPopover'
import { useAppDispatch, useAptConfig } from '../../hooks'
import { usePopover } from '../../hooks/usePopover'
import {
    createStatusGroup,
    CreateStatusGroupRequest,
    RequestCustomStatus,
    RootState,
    setInventoryConfigList,
    setStatusList,
    updateStatusGroupReq,
    UpdateStatusGroupRequest,
} from '../../store'
import { useStyles } from '../../styles'
import { StatusField } from './StatusField'
import { StatusGroup } from '../../models'
import { Selector } from '../../components/Selector/Selector'

interface Props {
    open: boolean
    handleClose: () => void
    statusGroup?: StatusGroup
}

export const StatusGroupSideDrawer = (props: Props) => {
    const { open, handleClose, statusGroup } = props

    return (
        <SideDrawer
            open={open}
            handleClose={() => handleClose()}
            title={
                statusGroup === undefined
                    ? 'New Status Group'
                    : 'Edit Status Group'
            }
        >
            <StatusGroupForm
                handleClose={() => handleClose()}
                statusGroup={statusGroup}
            />
        </SideDrawer>
    )
}

interface StatusGroupFormProps {
    handleClose: () => void
    modalMode?: boolean
    statusGroup?: StatusGroup
    setSelectedStatusGroup?: (statusGroup: StatusGroup) => void
    customTopColorPlacement?: number
    customLeftColorPlacement?: number
    height?: string
    hideHeader?: boolean
}

export const StatusGroupForm = (props: StatusGroupFormProps) => {
    const {
        handleClose,
        statusGroup,
        modalMode,
        setSelectedStatusGroup,
        customLeftColorPlacement,
        customTopColorPlacement,
        height,
        hideHeader,
    } = props

    const theme = useTheme()
    const dispatch = useAppDispatch()
    const classes = useStyles()

    const statusList = useSelector(
        (state: RootState) => state.aptConfig.statusList,
    )

    // An interface for the statuses the user will use/create in the drawer
    // these are also the same format the be wants them in for ease of sending the req

    const baseStatus: RequestCustomStatus = {
        id: -1,
        name: '',
        color: theme.palette.primary.main,
        causes_flag: false,
    }

    const CREATE_STATUS_ID = -2
    const NO_DEFAULT_STATUS_ID = -3

    const createStatus: RequestCustomStatus = {
        id: CREATE_STATUS_ID,
        name: 'Create New Status',
        color: theme.palette.primary.main,
        causes_flag: false,
    }

    const noDefault: RequestCustomStatus = {
        id: NO_DEFAULT_STATUS_ID,
        name: 'No Default',
        color: theme.palette.primary.main,
        causes_flag: false,
    }

    const [name, setName] = useState('')
    const [validate, setValidate] = useState(false)
    const [defaultStatus, setDefaultStatus] = useState<RequestCustomStatus>(
        noDefault,
    )
    const [otherStatuses, setOtherStatuses] = useState<RequestCustomStatus[]>([
        baseStatus,
    ])
    const [selectedStatuses, setSelectedStatuses] = useState<number[]>([])

    const [showCustomDefault, setShowCustomDefault] = useState(false)
    const [customDefaultStatus, setCustomDefaultStatus] = useState(baseStatus)

    // Edit status popover state
    const {
        isOpen: isEditStatusOpen,
        anchorEl: editStatusAnchorEl,
        handleOpen: handleEditStatusOpen,
        handleClose: handleEditStatusClose,
    } = usePopover()

    const [editingStatus, setEditingStatus] = useState<
        | { type: 'existing'; status: any }
        | { type: 'new'; status: RequestCustomStatus; index: number }
        | null
    >(null)
    const [statusName, setStatusName] = useState('')
    const [statusColor, setStatusColor] = useState('#000000')
    const [statusCausesFlag, setStatusCausesFlag] = useState(false)

    const {
        updateCustomStatusReq,
        createCustomStatusReq,
        customStatusList,
        inventoryConfigList,
    } = useAptConfig({
        customStatusList: true,
    })

    // Add status popover state
    const {
        isOpen: isAddStatusOpen,
        anchorEl: addStatusAnchorEl,
        handleOpen: handleAddStatusOpen,
        handleClose: handleAddStatusClose,
    } = usePopover()

    // Create new status popover state - use the same anchor as Add Status popover
    const {
        isOpen: isCreateStatusOpen,
        anchorEl: createStatusAnchorEl,
        handleOpen: handleCreateStatusOpen,
        handleClose: handleCreateStatusClose,
    } = usePopover()

    // Store the original anchor element for the create popover
    const [
        createStatusOriginalAnchor,
        setCreateStatusOriginalAnchor,
    ] = useState<HTMLElement | null>(null)

    // Use customStatusList from hook which is updated by createCustomStatusReq
    // Filter out UNINSPECTED_STATUS (id: -1) from customStatusList
    // Use useMemo to ensure it updates when customStatusList or statusList changes
    const availableStatusList = useMemo(() => {
        return (
            (customStatusList || statusList)?.filter((s) => s.id !== -1) ?? []
        )
    }, [customStatusList, statusList])

    // Get all statuses to display (default + selected existing + new)
    const getAllStatuses = () => {
        const allStatuses: Array<{
            type: 'default' | 'existing' | 'new'
            status: any
            index?: number
        }> = []

        // Add default status if it exists
        if (
            defaultStatus.id !== NO_DEFAULT_STATUS_ID &&
            defaultStatus.id !== CREATE_STATUS_ID
        ) {
            allStatuses.push({ type: 'default', status: defaultStatus })
        }

        // Add selected existing statuses
        selectedStatuses.forEach((id) => {
            const status = availableStatusList?.find((s) => s.id === id)
            if (status && status.id !== defaultStatus.id) {
                allStatuses.push({ type: 'existing', status })
            }
        })

        // Add new statuses
        otherStatuses.forEach((status, index) => {
            if (status.name || status.color !== theme.palette.primary.main) {
                allStatuses.push({ type: 'new', status, index })
            }
        })

        return allStatuses
    }

    const handleEditStatusClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        statusData: {
            type: 'default' | 'existing' | 'new'
            status: any
            index?: number
        },
    ) => {
        if (statusData.type === 'existing') {
            setEditingStatus({
                type: 'existing',
                status: statusData.status,
            })
            setStatusName(statusData.status.name)
            setStatusColor(statusData.status.color)
            setStatusCausesFlag(statusData.status.causes_flag)
        } else if (statusData.type === 'new') {
            setEditingStatus({
                type: 'new',
                status: statusData.status,
                index: statusData.index!,
            })
            setStatusName(statusData.status.name)
            setStatusColor(statusData.status.color)
            setStatusCausesFlag(statusData.status.causes_flag || false)
        } else if (statusData.type === 'default') {
            // Default status - check if it's from statusList or custom
            const statusFromList =
                availableStatusList?.find(
                    (s) => s.id === statusData.status.id,
                ) || statusList?.find((s) => s.id === statusData.status.id)
            if (statusFromList) {
                // It's an existing status, edit it
                setEditingStatus({
                    type: 'existing',
                    status: statusFromList,
                })
                setStatusName(statusFromList.name)
                setStatusColor(statusFromList.color)
                setStatusCausesFlag(statusFromList.causes_flag)
            } else if (defaultStatus.id === CREATE_STATUS_ID) {
                // It's a custom default status being created
                setEditingStatus({
                    type: 'new',
                    status: customDefaultStatus,
                    index: -1, // Special index for custom default
                })
                setStatusName(customDefaultStatus.name)
                setStatusColor(customDefaultStatus.color)
                setStatusCausesFlag(customDefaultStatus.causes_flag || false)
            } else {
                // Fallback - treat as existing
                setEditingStatus({
                    type: 'existing',
                    status: statusData.status,
                })
                setStatusName(statusData.status.name)
                setStatusColor(statusData.status.color)
                setStatusCausesFlag(statusData.status.causes_flag || false)
            }
        }
        handleEditStatusOpen(event)
    }

    const handleSaveStatus = (
        name: string,
        color: string,
        causesFlag: boolean,
    ) => {
        if (editingStatus?.type === 'existing') {
            // Update existing status via API
            updateCustomStatusReq({
                id: editingStatus.status.id,
                body: { name, color, causes_flag: causesFlag },
            })
                .then((updatedStatus) => {
                    // Update in statusList via Redux
                    const newStatusList = [...(statusList ?? [])]
                    const index = newStatusList.findIndex(
                        (s) => s.id === updatedStatus.id,
                    )
                    if (index !== -1) {
                        newStatusList[index] = updatedStatus
                        dispatch(setStatusList(newStatusList))
                    }
                    // If this was the default status, update it
                    if (defaultStatus.id === updatedStatus.id) {
                        setDefaultStatus(updatedStatus)
                    }
                    toast.success('Status updated successfully')
                    handleEditStatusClose()
                })
                .catch((e) => {
                    console.log('error', e)
                    toast.error(
                        e.response?.data?.message || 'Failed to update status',
                    )
                })
        }
    }

    useEffect(() => {
        if (statusGroup) {
            setName(statusGroup.name)
            setDefaultStatus(statusGroup.default_status ?? noDefault)
            setOtherStatuses([])
            const statusIds: number[] = []
            statusGroup.custom_statuses?.forEach((s) => {
                if (s.id !== statusGroup.default_status?.id) {
                    statusIds.push(s.id)
                }
            })

            setSelectedStatuses(statusIds)
        } else {
            setName('')
            setDefaultStatus(noDefault)
            setOtherStatuses([])
            setSelectedStatuses([])
        }
    }, [statusGroup])

    const handleDrawerClose = () => {
        handleClose()
        setName('')
        setValidate(false)
        setOtherStatuses([baseStatus])
        setDefaultStatus(noDefault)
        setSelectedStatuses([])
        setCustomDefaultStatus(baseStatus)
    }

    const saveButtonPress = () => {
        // see if we are in edit mode or create mode
        if (statusGroup) {
            // we're in edit mode
            // Validate: name must not be empty, and defaultStatus must be either NO_DEFAULT or a valid status
            const isDefaultStatusValid =
                defaultStatus.id === NO_DEFAULT_STATUS_ID ||
                (defaultStatus.id !== CREATE_STATUS_ID &&
                    defaultStatus.id !== baseStatus.id &&
                    defaultStatus.name !== '')

            if (name === '' || !isDefaultStatusValid) {
                setValidate(true)
            } else {
                const requestStatuses: RequestCustomStatus[] = []
                selectedStatuses.forEach((id) => {
                    const selected =
                        availableStatusList?.find((s) => s.id === id) ||
                        statusList?.find((s) => s.id === id)
                    if (selected) {
                        requestStatuses.push(selected)
                    }
                })
                otherStatuses.forEach((s) => {
                    if (s.name !== '') {
                        requestStatuses.push(s)
                    }
                })
                const req: UpdateStatusGroupRequest = {
                    params: { id: statusGroup.id },
                    body: { name: name, status_list: requestStatuses },
                }
                if (defaultStatus.id !== NO_DEFAULT_STATUS_ID) {
                    req.body.default_status = defaultStatus
                    req.body.status_list = [...requestStatuses, defaultStatus]
                } else {
                    req.body.remove_default_status = true
                }
                dispatch(updateStatusGroupReq(req)).then((res) => {
                    const newStatuses = [...(statusList ?? [])]
                    res.data.custom_statuses?.forEach((status) => {
                        const selected = statusList?.find(
                            (s) => s.id === status.id,
                        )
                        if (!selected) {
                            newStatuses.push(status)
                        }
                    })
                    dispatch(setStatusList(newStatuses))

                    // update the inventory configs that have this status group
                    const updatedInventoryConfigList = inventoryConfigList?.map(
                        (config) => {
                            if (config.status_group?.id === statusGroup?.id) {
                                return {
                                    ...config,
                                    status_group: res.data,
                                    custom_status_list:
                                        res.data.custom_statuses?.map((s) => ({
                                            ...s,
                                            cost:
                                                config.custom_status_list?.find(
                                                    (cs) => cs.id === s.id,
                                                )?.cost ?? 0,
                                        })) ?? [],
                                }
                            }
                            return config
                        },
                    )

                    dispatch(setInventoryConfigList(updatedInventoryConfigList))

                    // alert the user it was succesful, and close the drawer
                    toast.success(`${name} succesfully updated!`)
                    handleDrawerClose()
                })
            }
        } else {
            // create mode
            // make sure that the default status and the status group have a name
            // if they do, make the request to create a new status group
            // make sure default status is the corrent one between custom and existing
            const defStatus =
                defaultStatus.id === CREATE_STATUS_ID
                    ? customDefaultStatus
                    : defaultStatus

            // Validate: name must not be empty, and defaultStatus must be either NO_DEFAULT or a valid status with a name
            const isDefaultStatusValid =
                defStatus.id === NO_DEFAULT_STATUS_ID ||
                (defStatus.id !== CREATE_STATUS_ID &&
                    defStatus.id !== baseStatus.id &&
                    defStatus.name !== '')

            if (name === '' || !isDefaultStatusValid || !hasAttachedStatuses) {
                setValidate(true)
            } else {
                //generate the request body
                const req: CreateStatusGroupRequest = {
                    name: name,

                    statuses: [],
                }
                if (defStatus.id !== NO_DEFAULT_STATUS_ID) {
                    req.default_status = defStatus
                }

                // add the other statuses
                // if any of the other statuses name is '', assume that they are not to be sent to the server
                otherStatuses.forEach((s) => {
                    if (s.name !== '') {
                        req.statuses?.push(s)
                    }
                })
                // add other existing statuses
                selectedStatuses.forEach((id) => {
                    const selected = statusList?.find((s) => s.id === id)
                    if (selected) {
                        req.statuses?.push(selected)
                    }
                })

                // send the request
                dispatch(createStatusGroup(req)).then((res) => {
                    const newStatusGroup = res.data
                    const newStatuses = [...(statusList ?? [])]
                    newStatusGroup.custom_statuses?.forEach((status) => {
                        const selected = statusList?.find(
                            (s) => s.id === status.id,
                        )
                        if (!selected) {
                            newStatuses.push(status)
                        }
                    })
                    dispatch(setStatusList(newStatuses))

                    if (setSelectedStatusGroup) setSelectedStatusGroup(res.data)

                    // alert the user it was succesful, and close the drawer
                    toast.success(`${name} succesfully created!`)
                    handleDrawerClose()
                })
            }
        }
    }

    // const defaultStatusOptions: JSX.Element[] = []
    // defaultStatusOptions.push(
    //     <MenuItem
    //         value={NO_DEFAULT_STATUS_ID}
    //         key={'no-status-default-selector'}
    //     >
    //         No Default
    //     </MenuItem>,
    // )
    // defaultStatusOptions.push(
    //     <MenuItem
    //         value={CREATE_STATUS_ID}
    //         key={'create-new-status-default-selector'}
    //     >
    //         Create New Status
    //     </MenuItem>,
    // )
    // availableStatusList?.forEach((s) => {
    //     defaultStatusOptions.push(
    //         <MenuItem value={s.id} key={`status-default-selector-${s.id}`}>
    //             {s.name}
    //         </MenuItem>,
    //     )
    // })

    const defaultStatusOptionsData: RequestCustomStatus[] = []
    defaultStatusOptionsData.push({
        id: NO_DEFAULT_STATUS_ID,
        name: 'No Default',
        color: '',
        causes_flag: false,
    })
    defaultStatusOptionsData.push({
        id: CREATE_STATUS_ID,
        name: 'Create New Status',
        color: '',
        causes_flag: false,
    })
    availableStatusList?.forEach((s) => {
        defaultStatusOptionsData.push(s)
    })

    const SubHeaderCellStyle: React.CSSProperties = {
        fontWeight: 500,
        fontSize: '15px',
        marginBottom: theme.spacing(2),
    }

    const hasAttachedStatuses = getAllStatuses().length > 0

    return (
        <Container
            direction="column"
            flex={1}
            scrollY
            style={{ margin: !modalMode ? theme.spacing(0, 2, 0, 2) : '' }}
        >
            {!hideHeader && modalMode && !statusGroup && (
                <Container style={{ ...SubHeaderCellStyle, fontSize: '20px' }}>
                    Create New Status Group
                </Container>
            )}

            {!hideHeader && modalMode && statusGroup && (
                <Container style={{ ...SubHeaderCellStyle, fontSize: '20px' }}>
                    Edit Status Group
                </Container>
            )}

            <Container
                style={{
                    flexDirection: 'column',
                    maxHeight: height,
                    minHeight: height,
                    overflow: 'auto',
                }}
            >
                {/* Name */}
                <Container style={{ ...SubHeaderCellStyle }}>Name:</Container>

                <TextField
                    variant="outlined"
                    error={validate && name === ''}
                    value={name}
                    onChange={(e) => {
                        const name = e.target.value.slice(0, 30)
                        setName(name)
                    }}
                    style={{
                        marginBottom: theme.spacing(3),
                    }}
                    size="small"
                    helperText={validate && name === '' && 'Required'}
                    fullWidth
                />

                {/* Default Status Selector */}
                <Container style={{ ...SubHeaderCellStyle, marginTop: 0 }}>
                    Default Status
                </Container>

                <Selector
                    currentValue={defaultStatus.id}
                    data={defaultStatusOptionsData ?? []}
                    customStyle={{
                        formControl: {
                            marginBottom: theme.spacing(3),
                            width: '100%',
                        },
                    }}
                    size="small"
                    onChange={(e) => {
                        const id = e.target.value as number
                        if (id === CREATE_STATUS_ID) {
                            setDefaultStatus(createStatus)
                            setShowCustomDefault(true)
                        } else if (id === NO_DEFAULT_STATUS_ID) {
                            setDefaultStatus(noDefault)
                            setShowCustomDefault(false)
                        } else {
                            const availableStatusList = (
                                customStatusList || statusList
                            )?.filter((s) => s.id !== -1)
                            const status = availableStatusList?.find(
                                (s) => s.id === id,
                            )
                            setDefaultStatus(status ?? noDefault)
                            setShowCustomDefault(false)
                        }
                    }}
                    getDisplayString={(s: RequestCustomStatus) => `${s.name}`}
                    searchable={true}
                    popupHeight="500px"
                />

                {showCustomDefault && (
                    <Container style={{ marginBottom: theme.spacing(3) }}>
                        <StatusField
                            error={validate && customDefaultStatus.name === ''}
                            status={customDefaultStatus}
                            updateStatus={(s) => setCustomDefaultStatus(s)}
                            label={'New Default Status'}
                            customLeftPlacement={customLeftColorPlacement}
                            customTopPlacement={customTopColorPlacement}
                        />
                    </Container>
                )}

                {/* Statuses Display */}
                <Container direction="column">
                    <Container
                        style={{
                            ...SubHeaderCellStyle,
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        Statuses
                    </Container>

                    {/* Status Boxes Display - Like StatusGroupDisplay */}
                    <Container
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: theme.spacing(1),
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        {getAllStatuses().map((statusData) => {
                            const isDefault =
                                statusData.type === 'default' ||
                                (statusData.type === 'existing' &&
                                    statusData.status.id === defaultStatus.id)
                            const status =
                                statusData.type === 'existing'
                                    ? statusData.status
                                    : statusData.status

                            return (
                                <div
                                    key={`STATUS_${statusData.type}_${
                                        statusData.type === 'new'
                                            ? statusData.index
                                            : status.id
                                    }`}
                                    style={{
                                        padding: theme.spacing(1, 2),
                                        border: `1px solid black`,
                                        borderRadius: theme.shape.borderRadius,
                                        margin: theme.spacing(0.5),
                                        width: `calc((100% / 3) - ${theme.spacing(
                                            2,
                                        )}px)`,
                                        boxSizing: 'border-box',
                                        borderColor: isDefault ? '' : 'black',
                                        backgroundColor: isDefault
                                            ? status.color
                                            : 'white',
                                        color: isDefault ? 'white' : 'black',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        position: 'relative',
                                    }}
                                    onMouseEnter={(e) => {
                                        const editIcon = e.currentTarget.querySelector(
                                            '.status-edit-icon',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '1'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        const editIcon = e.currentTarget.querySelector(
                                            '.status-edit-icon',
                                        ) as HTMLElement
                                        if (editIcon) {
                                            editIcon.style.opacity = '0'
                                        }
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flex: 1,
                                        }}
                                    >
                                        <span
                                            style={{
                                                textAlign: 'center',
                                                alignSelf: 'center',
                                                fontWeight: isDefault
                                                    ? 600
                                                    : 400,
                                            }}
                                        >
                                            {status.name}
                                            {isDefault && ' (Default)'}
                                        </span>
                                        {(status.causes_flag ||
                                            (statusData.type === 'new' &&
                                                status.causes_flag)) && (
                                            <Tooltip title="Causes Flag">
                                                <FlagIcon
                                                    style={{
                                                        width: '17px',
                                                        height: '17px',
                                                        marginLeft: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                    <Container
                                        style={{
                                            display: 'flex',
                                            gap: theme.spacing(0.5),
                                        }}
                                    >
                                        <Tooltip title="Edit Status">
                                            <IconButton
                                                className="status-edit-icon"
                                                size="small"
                                                style={{
                                                    opacity: 0,
                                                    transition:
                                                        'opacity 0.2s ease-in-out',
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                    padding: theme.spacing(0.5),
                                                }}
                                                onClick={(e) => {
                                                    // For default status, check if it's from statusList or custom
                                                    if (
                                                        statusData.type ===
                                                        'default'
                                                    ) {
                                                        const statusFromList =
                                                            availableStatusList?.find(
                                                                (s) =>
                                                                    s.id ===
                                                                    statusData
                                                                        .status
                                                                        .id,
                                                            ) ||
                                                            statusList?.find(
                                                                (s) =>
                                                                    s.id ===
                                                                    statusData
                                                                        .status
                                                                        .id,
                                                            )
                                                        if (statusFromList) {
                                                            // It's an existing status, edit it
                                                            handleEditStatusClick(
                                                                e,
                                                                {
                                                                    type:
                                                                        'existing',
                                                                    status: statusFromList,
                                                                },
                                                            )
                                                        } else if (
                                                            defaultStatus.id ===
                                                            CREATE_STATUS_ID
                                                        ) {
                                                            // It's a custom default status being created
                                                            handleEditStatusClick(
                                                                e,
                                                                {
                                                                    type: 'new',
                                                                    status: customDefaultStatus,
                                                                    index: -1, // Special index for custom default
                                                                },
                                                            )
                                                        } else {
                                                            // Regular default status from list
                                                            handleEditStatusClick(
                                                                e,
                                                                {
                                                                    type:
                                                                        'existing',
                                                                    status:
                                                                        statusFromList ||
                                                                        statusData.status,
                                                                },
                                                            )
                                                        }
                                                    } else {
                                                        handleEditStatusClick(
                                                            e,
                                                            statusData,
                                                        )
                                                    }
                                                }}
                                            >
                                                <Edit
                                                    style={{
                                                        width: '16px',
                                                        height: '16px',
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                        {statusData.type === 'default' && (
                                            <Tooltip title="Remove Default Status">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setDefaultStatus(
                                                            noDefault,
                                                        )
                                                    }}
                                                    style={{
                                                        padding: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {statusData.type === 'new' && (
                                            <Tooltip title="Remove">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const newOtherStatuses = otherStatuses.filter(
                                                            (_, idx) =>
                                                                idx !==
                                                                statusData.index,
                                                        )
                                                        setOtherStatuses(
                                                            newOtherStatuses,
                                                        )
                                                    }}
                                                    style={{
                                                        padding: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {statusData.type === 'existing' && (
                                            <Tooltip title="Remove">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedStatuses(
                                                            selectedStatuses.filter(
                                                                (id) =>
                                                                    id !==
                                                                    status.id,
                                                            ),
                                                        )
                                                    }}
                                                    style={{
                                                        padding: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Container>
                                </div>
                            )
                        })}

                        {/* Add Status Button - Appears beside last status */}
                        <div
                            style={{
                                padding: theme.spacing(1, 2),
                                border: `2px dashed ${theme.palette.grey[400]}`,
                                borderRadius: theme.shape.borderRadius,
                                margin: theme.spacing(0.5),
                                width: `calc((100% / 3) - ${theme.spacing(
                                    2,
                                )}px)`,
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                backgroundColor: 'transparent',
                                minHeight: '40px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor =
                                    theme.palette.primary.main
                                e.currentTarget.style.backgroundColor =
                                    theme.palette.action.hover
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor =
                                    theme.palette.grey[400]
                                e.currentTarget.style.backgroundColor =
                                    'transparent'
                            }}
                            onClick={(e) => {
                                handleAddStatusOpen(e)
                                // Store the anchor element for use by Create Status popover
                                setCreateStatusOriginalAnchor(e.currentTarget)
                            }}
                        >
                            <Container
                                style={{
                                    display: 'flex',
                                    // flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                }}
                            >
                                <Add
                                    style={{
                                        color: theme.palette.grey[600],
                                        fontSize: '20px',
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: '14px',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Add Status
                                </span>
                            </Container>
                        </div>
                    </Container>
                    {!statusGroup && validate && !hasAttachedStatuses && (
                        <Container
                            style={{
                                color: theme.palette.error.main,
                                fontSize: '12px',
                                marginTop: -theme.spacing(1),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            At least one status is required
                        </Container>
                    )}
                </Container>

                {/* Add Status Popover */}
                <Popover
                    anchorEl={addStatusAnchorEl}
                    onClose={handleAddStatusClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    open={isAddStatusOpen}
                    PaperProps={{
                        style: {
                            borderRadius: theme.shape.borderRadius * 2,
                            boxShadow: theme.shadows[8],
                        },
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            padding: theme.spacing(2),
                            minWidth: '250px',
                        }}
                    >
                        <Button
                            fullWidth
                            startIcon={<Add />}
                            onClick={(e) => {
                                e.stopPropagation()
                                // Store the original anchor from the Add Status popover
                                setCreateStatusOriginalAnchor(addStatusAnchorEl)
                                handleAddStatusClose()
                                // Use setTimeout to ensure the first popover closes before opening the second
                                setTimeout(() => {
                                    // Use the original anchor element from the Add Status popover
                                    if (addStatusAnchorEl) {
                                        const syntheticEvent = ({
                                            currentTarget: addStatusAnchorEl,
                                        } as unknown) as React.MouseEvent<HTMLElement>
                                        handleCreateStatusOpen(syntheticEvent)
                                    }
                                }, 0)
                            }}
                            style={{
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                marginBottom: theme.spacing(1),
                            }}
                            color="primary"
                        >
                            Create New Status
                        </Button>
                        <Divider style={{ margin: theme.spacing(1, 0) }} />

                        <Selector
                            currentValue=""
                            data={availableStatusList ?? []}
                            getDisplayString={(s: RequestCustomStatus) =>
                                `${s.name}`
                            }
                            onChange={(e) => {
                                const id = e.target.value as number
                                if (id && !selectedStatuses.includes(id)) {
                                    setSelectedStatuses([
                                        ...selectedStatuses,
                                        id,
                                    ])
                                    handleAddStatusClose()
                                }
                            }}
                            label="Add Existing Status"
                            searchable={true}
                            size="small"
                            popupHeight="500px"
                            customStyle={{
                                formControl: {
                                    width: '100%',
                                },
                            }}
                        />
                    </Container>
                </Popover>

                {/* Create New Status Popover */}
                <CreateStatusPopover
                    open={isCreateStatusOpen}
                    anchorEl={
                        createStatusOriginalAnchor || createStatusAnchorEl
                    }
                    onClose={handleCreateStatusClose}
                    onSave={async (name, color, causesFlag) => {
                        try {
                            const newStatus = await createCustomStatusReq({
                                id: -1,
                                name,
                                color,
                                causes_flag: causesFlag,
                            })
                            // Add the newly created status to the selected statuses
                            // so it appears in the status group
                            setSelectedStatuses((prev) => [
                                ...prev,
                                newStatus.id,
                            ])
                            toast.success('Status created successfully')
                            handleCreateStatusClose()
                        } catch (e: any) {
                            console.log('error', e)
                            toast.error(
                                e.response?.data?.message ||
                                    'Failed to create status',
                            )
                        }
                    }}
                    theme={theme}
                />

                {/* Edit Status Popover */}
                <EditStatusPopover
                    open={isEditStatusOpen}
                    anchorEl={editStatusAnchorEl}
                    onClose={handleEditStatusClose}
                    statusName={statusName}
                    statusColor={statusColor}
                    statusCausesFlag={statusCausesFlag}
                    onSave={handleSaveStatus}
                    theme={theme}
                />
            </Container>
            {/* save and cancel buttons */}

            <Container
                justifyContent={'flex-end'}
                style={{
                    marginTop: theme.spacing(1),
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    style={{
                        textTransform: 'none',
                        margin: theme.spacing(1, 0, 1, 2),
                    }}
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    style={{
                        margin: theme.spacing(1, 0, 1, 2),
                        backgroundColor: '#008C85',
                        color: 'white',
                        textTransform: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={saveButtonPress}
                >
                    Save
                </Button>
            </Container>
        </Container>
    )
}
